import { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { motion } from "framer-motion";
import { Camera, X, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const BarcodeScanner = ({ 
  isOpen, 
  onClose, 
  onScan,
  title = "Barcode scannen"
}) => {
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    
    if (isOpen) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (mountedRef.current) {
          startScanner();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
    
    return () => {
      mountedRef.current = false;
      stopScanner();
    };
  }, [isOpen]);

  const startScanner = async () => {
    if (!scannerRef.current || html5QrCodeRef.current) return;
    
    try {
      setError("");
      setPermissionDenied(false);
      setScanning(true);
      
      // Request camera permission first
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: "environment" } 
        });
        // Stop the stream immediately - we just needed permission
        stream.getTracks().forEach(track => track.stop());
      } catch (permErr) {
        console.error("Permission error:", permErr);
        if (permErr.name === "NotAllowedError" || permErr.name === "PermissionDeniedError") {
          setPermissionDenied(true);
          setError("Kamera-Zugriff verweigert. Bitte erlauben Sie den Zugriff in den App-Einstellungen.");
        } else {
          setError("Kamera nicht verfügbar: " + permErr.message);
        }
        setScanning(false);
        return;
      }
      
      html5QrCodeRef.current = new Html5Qrcode("barcode-reader");
      
      // Get available cameras
      const cameras = await Html5Qrcode.getCameras();
      
      if (!cameras || cameras.length === 0) {
        setError("Keine Kamera gefunden.");
        setScanning(false);
        return;
      }
      
      // Prefer back camera
      const backCamera = cameras.find(c => 
        c.label.toLowerCase().includes("back") || 
        c.label.toLowerCase().includes("rear") ||
        c.label.toLowerCase().includes("rück")
      );
      const cameraId = backCamera?.id || cameras[cameras.length - 1].id;
      
      await html5QrCodeRef.current.start(
        cameraId,
        {
          fps: 10,
          qrbox: { width: 250, height: 150 },
          aspectRatio: 1.333,
        },
        (decodedText) => {
          if (mountedRef.current) {
            handleScanSuccess(decodedText);
          }
        },
        () => {
          // Ignore scan errors - no code detected yet
        }
      );
    } catch (err) {
      console.error("Scanner error:", err);
      if (mountedRef.current) {
        if (err.message?.includes("NotAllowed") || err.message?.includes("Permission")) {
          setPermissionDenied(true);
          setError("Kamera-Zugriff verweigert. Bitte erlauben Sie den Zugriff in den App-Einstellungen.");
        } else {
          setError("Kamera konnte nicht gestartet werden: " + (err.message || "Unbekannter Fehler"));
        }
        setScanning(false);
      }
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING state
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const handleScanSuccess = async (code) => {
    // Vibrate on mobile
    if (navigator.vibrate) {
      navigator.vibrate(100);
    }
    
    await stopScanner();
    onScan(code);
    onClose();
  };

  const handleClose = async () => {
    await stopScanner();
    onClose();
  };

  const openAppSettings = () => {
    // On Android, this hint helps the user
    alert("Bitte öffnen Sie die App-Einstellungen:\n\n1. Lange auf das App-Icon tippen\n2. 'App-Info' wählen\n3. 'Berechtigungen' → 'Kamera' aktivieren\n\nDanach die App neu starten.");
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-rose-600" />
            {title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="relative bg-black min-h-[300px]">
          {/* Scanner Container */}
          <div 
            id="barcode-reader" 
            ref={scannerRef}
            className="w-full"
          />
          
          {/* Overlay with scan area indicator */}
          {scanning && !error && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 bg-black/40" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-32 bg-transparent border-2 border-rose-500 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.4)]">
                <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-rose-500 rounded-tl" />
                <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-rose-500 rounded-tr" />
                <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-rose-500 rounded-bl" />
                <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-rose-500 rounded-br" />
                <motion.div
                  className="absolute left-2 right-2 h-0.5 bg-rose-500"
                  initial={{ top: 4 }}
                  animate={{ top: ["4%", "96%", "4%"] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
              </div>
            </div>
          )}
          
          {/* Error Message */}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/90 p-4">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
                <p className="text-white text-sm mb-4">{error}</p>
                {permissionDenied ? (
                  <div className="space-y-2">
                    <Button 
                      onClick={openAppSettings}
                      className="bg-rose-600 hover:bg-rose-700 w-full"
                    >
                      Einstellungen öffnen
                    </Button>
                    <Button 
                      onClick={handleClose}
                      variant="outline"
                      className="w-full border-slate-600 text-white hover:bg-slate-800"
                    >
                      Schließen
                    </Button>
                  </div>
                ) : (
                  <Button 
                    onClick={startScanner} 
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    Erneut versuchen
                  </Button>
                )}
              </div>
            </div>
          )}
          
          {/* Loading state */}
          {!scanning && !error && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-white text-sm">Kamera wird gestartet...</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Instructions */}
        <div className="p-4 bg-slate-50 border-t">
          <p className="text-sm text-slate-600 text-center">
            Halten Sie den Barcode in den Rahmen
          </p>
        </div>
        
        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 text-slate-500 hover:text-slate-700"
          onClick={handleClose}
        >
          <X className="w-5 h-5" />
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScanner;
